import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { useErrorReport } from './ErrorReportContext';
import { unreadInboxSummary } from '../pages/friends/chat/chatInboxState';
import { hydrateChatPartners, subscribeUserChats } from '../services/chat/chatApi';

const EMPTY = {
  chats: [],
  partners: {},
  unreadChats: [],
  unreadTotal: 0,
  loading: false,
};

const ChatInboxContext = createContext(EMPTY);

export function useChatInbox() {
  return useContext(ChatInboxContext);
}

function partnerKey(chats, uid) {
  return (chats || [])
    .map((chat) => {
      const members = chat?.memberUids || [];
      return members.find((id) => id && id !== uid) || '';
    })
    .filter(Boolean)
    .sort()
    .join('|');
}

export function ChatInboxProvider({ children }) {
  const { currentUser } = useAuth();
  const { reportError } = useErrorReport();
  const uid = currentUser?.uid || null;
  const [chats, setChats] = useState([]);
  const [partners, setPartners] = useState({});
  const [loading, setLoading] = useState(Boolean(uid));
  const hydrateKeyRef = useRef('');

  useEffect(() => {
    if (!uid) {
      setChats([]);
      setPartners({});
      setLoading(false);
      hydrateKeyRef.current = '';
      return undefined;
    }
    setLoading(true);
    return subscribeUserChats(
      uid,
      (rows) => {
        setChats(rows || []);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        reportError(err, {
          pageLabel: 'Chat',
          source: 'chat-inbox-subscribe',
          userMessage: 'Could not load chats.',
          silent: true,
        });
      }
    );
  }, [uid, reportError]);

  useEffect(() => {
    if (!uid) return undefined;
    const key = partnerKey(chats, uid);
    if (key === hydrateKeyRef.current) return undefined;
    hydrateKeyRef.current = key;
    if (!key) {
      setPartners({});
      return undefined;
    }
    let cancelled = false;
    hydrateChatPartners(chats, uid)
      .then((map) => {
        if (!cancelled) setPartners(map || {});
      })
      .catch((err) => {
        if (cancelled) return;
        reportError(err, {
          pageLabel: 'Chat',
          source: 'chat-inbox-partners',
          userMessage: 'Could not load chat names.',
          silent: true,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [chats, uid, reportError]);

  const value = useMemo(() => {
    if (!uid) return EMPTY;
    const { unreadChats, unreadTotal } = unreadInboxSummary(chats, uid);
    return {
      chats,
      partners,
      unreadChats,
      unreadTotal,
      loading,
    };
  }, [chats, partners, loading, uid]);

  return (
    <ChatInboxContext.Provider value={value}>{children}</ChatInboxContext.Provider>
  );
}
